import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { TOKENS_PER_PAGE } from "config"
import { OrderDirection } from "utils/types"
import { parseScriptType, parseAspectRatio } from "utils/scriptJSON"
import CollectionFeatureFilters from "components/CollectionFeatureFilters"
import ProjectDate from "components/ProjectDate"
import ProjectExplore from "components/ProjectExplore"
import MetadataAwareTokenView from "components/MetadataAwareTokenView"
import MetadataTokenGrid from "components/MetadataTokenGrid"
import Tokens from "components/Tokens"
import Loading from "components/Loading"
import Collapsible from "components/Collapsible"
import useArtBlocksProjectFeatureFacets from "hooks/useArtBlocksProjectFeatureFacets"
import useArtBlocksProjectTokenPage from "hooks/useArtBlocksProjectTokenPage"
import usePageMeta from "hooks/usePageMeta"
import useProject from "hooks/useProject"
import useWindowSize from "hooks/useWindowSize"
import { getContractConfigByAddress } from "utils/contractInfoHelper"
import EditProjectButton from "components/EditProjectButton"
import { useAccount } from "wagmi"
import MintingInterfaceFilter from "components/MintingInterfaceFilter"
import {
  collaboratorLabel,
  formatLibrary,
  formatSupply,
  getProjectStatus,
  isEffectivelyOpenEdition,
  isDisplayComplete,
  formatNumber,
  supplyProgress,
} from "utils/projectDisplay"
import {
  decodeFeatureFilters,
  encodeFeatureFilters,
  hasFeatureFilters,
  updateSearchParamsWithFeatureFilters,
} from "utils/featureFilters"
import { getArtworkFramePadding, getCollectionGridTokenWidth, getGalleryContainerInnerWidth } from "utils/galleryLayout"
import PlatformBadgeRow from "components/PlatformBadgeRow"
import { getProjectPlatformBadges } from "data/platforms"

interface Props {
  contractAddress: string
  id: string
}

const ProjectDetails = ({ contractAddress, id }: Props) => {
  const windowSize = useWindowSize()
  const { address } = useAccount()
  const { loading, error, data } = useProject(`${contractAddress}-${id}`)
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentPage, setCurrentPage] = useState(0)
  const [orderDirection, setOrderDirection] = useState(OrderDirection.ASC)
  const project = data?.project
  const token = project?.tokens[0]
  const contractConfig = getContractConfigByAddress(contractAddress)
  const chainId = contractConfig?.CHAIN_ID
  const featureFilters = useMemo(() => decodeFeatureFilters(searchParams), [searchParams])
  const featureFilterKey = useMemo(() => encodeFeatureFilters(featureFilters), [featureFilters])
  const tokenPageResponse = useArtBlocksProjectTokenPage({
    contractAddress,
    projectId: id,
    chainId,
    limit: TOKENS_PER_PAGE,
    offset: currentPage * TOKENS_PER_PAGE,
    orderDirection,
    featureFilters,
  })
  const facetsResponse = useArtBlocksProjectFeatureFacets(contractAddress, id, chainId)
  const hasFeatureFilterPanel = facetsResponse.loading || (facetsResponse.data?.facets?.length || 0) > 0
  const ogImage = token?.tokenId && contractConfig?.CHAIN_ID
    ? `https://media-proxy.artblocks.io/${contractConfig.CHAIN_ID}/${contractAddress.toLowerCase()}/${token.tokenId}.png`
    : undefined
  usePageMeta(
    project?.name ? `${project.name} — r4v3n` : undefined,
    project?.description || undefined,
    ogImage,
    project ? `/project/${contractAddress}/${id}` : undefined,
  )
  const platformBadges = getProjectPlatformBadges(contractAddress, id, { chainId })
  const artFramePadding = getArtworkFramePadding(windowSize.width)
  // Keep the representative piece within the viewport so it never clips, while
  // leaving room for the breadcrumb + the project info beside it.
  const featuredMaxHeight =
    windowSize.height > 0
      ? Math.min(windowSize.height * 0.72, Math.max(300, windowSize.height - 240))
      : undefined

  // Hero column: two-up at md+ (gap-12 = 48px), single column below — sized so
  // the frame (art + equal matte) fits the column exactly.
  const heroColumnWidth =
    windowSize.width >= 768
      ? (getGalleryContainerInnerWidth(windowSize.width) - 48) / 2
      : getGalleryContainerInnerWidth(windowSize.width)
  const previewWidth = Math.max(180, heroColumnWidth - artFramePadding)

  useEffect(() => {
    setCurrentPage(0)
  }, [featureFilterKey])

  if (error) return <p className="text-sm text-red-500">Error loading project.</p>
  if (loading) return <Loading />
  if (!project || !contractConfig) return null

  const metadataTokens = tokenPageResponse.data?.tokens || []
  const tokenCount = tokenPageResponse.data?.tokens_aggregate?.aggregate?.count ?? Number(project.invocations)
  const totalPages = Math.max(1, Math.ceil(tokenCount / TOKENS_PER_PAGE))
  const totalCollectionCount = facetsResponse.data?.totalCount || Number(project.invocations)
  const filteredByFeatures = hasFeatureFilters(featureFilters)
  const shouldUseLegacyTokenGrid =
    !filteredByFeatures && !tokenPageResponse.loading && metadataTokens.length === 0
  const status = getProjectStatus(project)
  const progress = supplyProgress(project)
  const openEdition = isEffectivelyOpenEdition(project)
  const displayComplete = isDisplayComplete(project)
  const projectAspectRatio = project.aspectRatio || parseAspectRatio(project.scriptJSON) || 1
  const tokenWidth = getCollectionGridTokenWidth(windowSize.width)

  const handleFeatureFiltersChange = (nextFilters: typeof featureFilters) => {
    setCurrentPage(0)
    setSearchParams(updateSearchParamsWithFeatureFilters(searchParams, nextFilters))
  }

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(0, Math.min(page, totalPages - 1)))
    window.requestAnimationFrame(() => {
      document.getElementById("project-works")?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }

  return (
    <div>
      <nav className="flex items-center gap-2 mb-10" aria-label="Breadcrumb">
        <a href="/projects" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">
          Projects
        </a>
        <span className="text-gallery-border">·</span>
        <span className="gallery-label text-gallery-text">{project.name}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-12 md:mb-16 md:items-center">
        {token && (
          <div className="flex justify-center md:justify-start">
            <div className="gallery-art-shell">
              <div className="gallery-art-frame">
                <MetadataAwareTokenView
                  contractAddress={contractConfig.CORE_CONTRACT_ADDRESS}
                  tokenId={token.tokenId}
                  chainId={chainId}
                  width={Math.floor(previewWidth)}
                  fallbackAspectRatio={projectAspectRatio}
                  maxHeight={featuredMaxHeight}
                  live
                  showInvocationLink={false}
                  loupe
                  loupeZoom={3}
                />
              </div>
              <div className="flex justify-end mt-2">
                <a
                  href={`/token/${contractAddress}/${token.tokenId}`}
                  className="gallery-label gallery-link text-gallery-muted hover:text-gallery-text"
                >
                  No. {token.invocation?.toString()}
                </a>
              </div>
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between gap-4 mb-4">
            <ProjectDate startTime={project?.minterConfiguration?.startTime!} />
            <span className="gallery-status">{status}</span>
          </div>
          <h1 className="gallery-heading mb-2">{project.name}</h1>
          <p className="artist-name text-gallery-muted text-lg mb-4">{collaboratorLabel(project.artistName)}</p>
          <PlatformBadgeRow badges={platformBadges} className="mb-7" />

          <div className="mb-6 text-sm leading-relaxed">
            <Collapsible content={project.description} />
          </div>

          <dl className="gallery-meta-grid mb-6">
            <div className="gallery-meta-cell">
              <dt>Supply</dt>
              <dd>{formatSupply(project)}</dd>
            </div>
            <div className="gallery-meta-cell">
              <dt>Edition</dt>
              <dd>{displayComplete ? "Sold Out" : openEdition ? "Open" : "Fixed"}</dd>
            </div>
            <div className="gallery-meta-cell">
              <dt>License</dt>
              <dd>{project.license || "Unspecified"}</dd>
            </div>
            <div className="gallery-meta-cell">
              <dt>Library</dt>
              <dd>{formatLibrary(parseScriptType(project.scriptJSON) || project.scriptTypeAndVersion)}</dd>
            </div>
          </dl>

          {/* Consistent sale treatment on every series: a bar for capped
              editions, a muted "open" bar for open editions. */}
          <div className="mb-6">
            <div className="gallery-card-foot mb-2">
              <span>Sold</span>
              <span>{displayComplete ? "Sold Out" : openEdition ? "Open edition" : `${progress}%`}</span>
            </div>
            <div
              className="gallery-progress"
              data-open={openEdition ? "true" : undefined}
              aria-label={openEdition ? `${formatSupply(project)}, open edition` : `${progress}% sold`}
            >
              <span style={{ width: openEdition ? "100%" : `${progress}%` }} />
            </div>
          </div>

          <div className="mb-6">
            <ProjectExplore project={project} />
          </div>

          {contractConfig.EDIT_PROJECT_URL && address?.toLowerCase() === project.artistAddress && (
            <EditProjectButton
              contractAddress={contractAddress}
              projectId={project.projectId}
              editProjectUrl={contractConfig.EDIT_PROJECT_URL}
            />
          )}

          {contractConfig.MINT_CONTRACT_ADDRESS !== "" && (
            <MintingInterfaceFilter
              contractVersion={contractConfig.CONTRACT_VERSION}
              coreContractAddress={contractAddress}
              mintContractAddress={contractConfig.MINT_CONTRACT_ADDRESS}
              projectId={project.projectId}
              artistAddress={project.artistAddress}
              scriptAspectRatio={project.aspectRatio || parseAspectRatio(project.scriptJSON)}
            />
          )}
        </div>
      </div>

      <div id="project-works" className="border-t border-gallery-border pt-10">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-serif text-2xl font-normal">
            {formatNumber(tokenCount)} {tokenCount === 1 ? "Work" : "Works"}
          </h2>
          <label className="gallery-select-wrap">
            <span>Sort</span>
            <select
              aria-label="Sort works"
              value={orderDirection}
              onChange={(e) => {
                setCurrentPage(0)
                setOrderDirection(e.target.value as OrderDirection)
              }}
              className="gallery-select"
            >
              <option value={OrderDirection.DESC}>Latest</option>
              <option value={OrderDirection.ASC}>Earliest</option>
            </select>
          </label>
        </div>

        <div className={hasFeatureFilterPanel ? "grid grid-cols-1 lg:grid-cols-[16rem_1fr] gap-8" : ""}>
          {hasFeatureFilterPanel && (
            <CollectionFeatureFilters
              facets={facetsResponse.data?.facets || []}
              selectedFilters={featureFilters}
              loading={facetsResponse.loading}
              error={facetsResponse.error}
              resultCount={tokenCount}
              totalCount={totalCollectionCount}
              truncated={facetsResponse.data?.truncated}
              onChange={handleFeatureFiltersChange}
            />
          )}

          <div>
            {tokenPageResponse.error && !shouldUseLegacyTokenGrid && (
              <div className="gallery-empty text-red-500">Error loading works.</div>
            )}
            {tokenPageResponse.loading && metadataTokens.length === 0 && <Loading />}
            {metadataTokens.length > 0 && (
              <MetadataTokenGrid
                tokens={metadataTokens}
                tokenWidth={tokenWidth}
                fallbackAspectRatio={projectAspectRatio}
                contractAddress={contractAddress}
                chainId={chainId}
                getTokenHref={(metadataToken) => `/token/${contractAddress}/${metadataToken.token_id}`}
              />
            )}
            {shouldUseLegacyTokenGrid && (
              <Tokens
                contractAddress={contractAddress}
                projectId={`${contractAddress.toLowerCase()}-${id}`}
                chainId={chainId}
                first={TOKENS_PER_PAGE}
                skip={currentPage * TOKENS_PER_PAGE}
                orderDirection={orderDirection}
                aspectRatio={projectAspectRatio}
              />
            )}
            {!tokenPageResponse.loading && !tokenPageResponse.error && filteredByFeatures && metadataTokens.length === 0 && (
              <div className="gallery-empty">No works match the selected features.</div>
            )}
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-12 mb-8">
            <button
              aria-label="Previous works page"
              disabled={currentPage === 0}
              onClick={() => goToPage(currentPage - 1)}
              className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
            >
              ← Prev
            </button>
            <span className="gallery-label text-gallery-muted">
              {currentPage + 1} / {totalPages}
            </span>
            <button
              aria-label="Next works page"
              disabled={currentPage === totalPages - 1}
              onClick={() => goToPage(currentPage + 1)}
              className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProjectDetails
